import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { DataGridProps as MuiDataGridProps } from '@mui/x-data-grid';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import {
  gridPageCountSelector,
  gridPageSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, CircularProgress } from '@monorepo/nova-ui';

// Default custom no rows overlay component
export const CustomNoRowsOverlay = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation('common');

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        height: '100%',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <SystemIcon iconName="SmileyXEyes" size={60} />
      <Typography
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontWeight: 400,
          fontStyle: 'italic',
          fontSize: theme.typography.pxToRem(48),
        })}
      >
        {t('no_results_found')}
      </Typography>
      <Typography
        sx={(theme) => ({
          color: theme.palette.text.main,
          fontWeight: 400,
          fontSize: theme.typography.pxToRem(19),
        })}
      >
        {t('try_other_combination')}
      </Typography>
      {onRetry && (
        <Button variant="primary" size="md" onClick={onRetry}>
          {t('retry')}
        </Button>
      )}
    </Stack>
  );
};

// Default custom loading overlay component
function CustomLoadingOverlay() {
  const { t } = useTranslation('common');

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'background.default',
        zIndex: 1,
      }}
    >
      <Typography
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontWeight: 400,
          fontStyle: 'italic',
          fontSize: theme.typography.pxToRem(48),
        })}
      >
        {t('just_a_moment')}
      </Typography>
      <Typography
        sx={(theme) => ({
          color: theme.palette.text.main,
          fontWeight: 400,
          fontSize: theme.typography.pxToRem(19),
          marginBottom: theme.spacing(2),
        })}
      >
        {t('loading_data')}
      </Typography>
      <CircularProgress
        size={48}
        thickness={3.6}
        sx={(theme) => ({
          color: theme.palette.primary.main,
        })}
      />
    </Stack>
  );
}

// Default custom pagination component for all DataGrid instances
function CustomPagination() {
  const { t } = useTranslation('common');
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    apiRef.current.setPage(value - 1);
  };

  const handlePageSizeChange = (event: { target: { value: unknown } }) => {
    apiRef.current.setPageSize(Number(event.target.value));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100% !important',
        maxWidth: '100% !important',
        minWidth: '100%',
        px: { xs: 1, sm: 2 }, // Responsive padding
        py: { xs: 1.5, sm: 1 }, // More padding on mobile
        minHeight: '56px', // Ensure consistent height
        flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
        gap: { xs: 2, sm: 0 }, // Gap between elements on mobile
        boxSizing: 'border-box',
        margin: 0,
      }}
    >
      {/* Rows per page */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          justifyContent: { xs: 'center', sm: 'flex-start' }, // Center on mobile
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller text on mobile
            whiteSpace: 'nowrap',
          }}
        >
          {t('rows_per_page', 'Rows per page:')}
        </Typography>
        <FormControl size="small" variant="standard">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              '&:before': { display: 'none' },
              '&:after': { display: 'none' },
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Page numbers */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-end' }, // Center on mobile
          alignItems: 'center',
        }}
      >
        <Pagination
          count={pageCount}
          page={page + 1}
          onChange={handlePageChange}
          color="primary"
          variant="outlined"
          shape="rounded"
          size="small" // Small size for mobile-friendly design
          siblingCount={0} // Show fewer page numbers on small screens
          boundaryCount={1}
          sx={{
            '& .MuiPaginationItem-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minWidth: { xs: '28px', sm: '32px' },
              height: { xs: '28px', sm: '32px' },
              margin: { xs: '0 1px', sm: '0 2px' }, // Tighter spacing on mobile
            },
          }}
        />
      </Box>
    </Box>
  );
}

export interface DataGridProps extends MuiDataGridProps {
  onRetry?: () => void;
}

export const DataGrid = ({ onRetry, ...props }: DataGridProps) => {
  // Create a wrapper component for the no rows overlay that has access to onRetry
  const NoRowsOverlayWrapper = () => <CustomNoRowsOverlay onRetry={onRetry} />;

  // Automatically use custom pagination, loading overlay, and no rows overlay if no custom slots are provided
  // IMPORTANT: We need to merge slots at the property level, not object level.
  // If we used ` {...defaultProps} {...props}`,
  // the entire slots object would get overridden, losing our default noRowsOverlay etc..
  // This approach preserves default slots while allowing individual slot overrides.
  const mergedSlots = {
    pagination: CustomPagination,
    loadingOverlay: CustomLoadingOverlay,
    noRowsOverlay: NoRowsOverlayWrapper,
    ...props.slots, // Allow overriding individual slots
  };

  const mergedProps = {
    ...props,
    slots: mergedSlots,
  };

  return <MuiDataGrid {...mergedProps} />;
};
